// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — SelectCheckbox
//
// Minimal, accessible checkbox used for bulk multi-select in the Lead/Deal/Task
// lists. Supports an `indeterminate` state for the header "select all" control.
// The shared UI kit has no checkbox, so this small primitive lives with the bulk
// feature it serves. It stops click propagation so selecting a row never triggers
// the row's own click/open handler.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef } from 'react';
import { cn } from '@/shared/utils/utils';

export interface SelectCheckboxProps {
  checked: boolean;
  /** Renders the tri-state dash; visually distinct from checked/unchecked. */
  indeterminate?: boolean;
  onChange: () => void;
  /** Accessible label (required — the checkbox has no visible text). */
  ariaLabel: string;
  className?: string;
}

export function SelectCheckbox({
  checked,
  indeterminate = false,
  onChange,
  ariaLabel,
  className,
}: SelectCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);

  // `indeterminate` is a DOM-only property, not an attribute — set it imperatively.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      aria-label={ariaLabel}
      onClick={(e) => e.stopPropagation()}
      onChange={onChange}
      className={cn(
        'h-4 w-4 cursor-pointer rounded border-outline text-primary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        className,
      )}
    />
  );
}
